import { Drawer, Text } from "@mantine/core"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  CreateBusinessPartnerRequestDto,
  UpdateBusinessPartnerRequestDto,
  useFindTags,
} from "~/gen/api"
import { TypesForm } from "~/pages/Admin/components/TypesForm"
import { FormConfig } from "~/pages/Admin/components/form.types"
import { formSchema as contactFormSchema } from "~/pages/Brand/Suppliers/SuppliersRepository/Suppliers"
import { SupplierFormValues } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/columns"
import {
  defaultUpdatePermissions,
  UpdatePermissions,
  useFields,
} from "~/pages/Brand/Suppliers/SuppliersRepository/utils/useFormFieldConfig"
import ProductionSitesInformation from "~/pages/SupplierPortal/ProductionSites/components/ProductionSitesInformation"

type SupplierMapping =
  | CreateBusinessPartnerRequestDto
  | UpdateBusinessPartnerRequestDto

type ContactDrawerProps = {
  opened: boolean
  setModalOpened: {
    readonly open: () => void
    readonly close: () => void
    readonly toggle: () => void
  }
  supplierMapping: SupplierMapping
  handleSupplierMapping: (values: SupplierMapping) => void
  isUpdate: boolean
  updatePermissions?: UpdatePermissions
}

export const ContactDrawer = ({
  opened,
  setModalOpened,
  supplierMapping,
  handleSupplierMapping,
  isUpdate = false,
  updatePermissions = defaultUpdatePermissions,
}: ContactDrawerProps) => {
  const { t } = useTranslation(["organizationSettings"])
  const { data: tagsData } = useFindTags()

  const tagOptions = useMemo(() => {
    return (
      tagsData?.data?.tags.map((tag) => ({
        label: tag.name,
        value: tag.name,
      })) ?? []
    )
  }, [tagsData])

  const fields = useFields(isUpdate, tagOptions, updatePermissions)

  const normalizedInitialValues = useMemo<SupplierFormValues>(() => {
    return {
      ...supplierMapping,
      supplierId:
        "supplierId" in supplierMapping
          ? (supplierMapping.supplierId ?? "")
          : "",
    }
  }, [supplierMapping])

  const formConfig = useMemo<FormConfig<SupplierFormValues>>(
    () => ({
      validate: zod4Resolver(contactFormSchema),
      initialValues: normalizedInitialValues,
    }),
    [normalizedInitialValues]
  )
  const typesFormKey = useMemo(() => {
    const supplierId =
      "supplierId" in supplierMapping ? (supplierMapping.supplierId ?? "") : ""
    return `${isUpdate ? "update" : "create"}-${supplierId}-${supplierMapping.email}`
  }, [isUpdate, supplierMapping])

  return (
    <Drawer
      position="right"
      opened={opened}
      onClose={setModalOpened.close}
      title={
        <Text component="span" fw={700} fz="h2">
          {t("supplierSettings.title")}
        </Text>
      }
      size="xl"
    >
      <TypesForm
        key={typesFormKey}
        readonly={updatePermissions.readonly}
        elements={fields}
        cancelLabel={t("supplierSettings.cancel")}
        submitLabel={t("supplierSettings.addSupplier")}
        formConfig={formConfig}
        mutate={handleSupplierMapping}
        closeDrawer={setModalOpened.close}
      >
        {"supplierId" in supplierMapping && supplierMapping.supplierId && (
          <ProductionSitesInformation supplierEmail={supplierMapping.email} />
        )}
      </TypesForm>
    </Drawer>
  )
}
